import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import { axiosConfig } from "../../../axios/axiosConfig";
import toast from "react-hot-toast";

const initialData = {
  android: {
    latest_version: "1.2.0",
    minimum_supported_version: "1.1.0",
    force_update: true,
    update_title: "Update Required",
    update_message:
      "A new version of StockYaari is available. Please update to continue using the app.",
    store_url:
      "https://play.google.com/store/apps/details?id=com.stockyaari.app",
  },
  ios: {
    latest_version: "1.1.5",
    minimum_supported_version: "1.0.8",
    force_update: false,
    update_title: "Update Available",
    update_message: "New features and performance improvements are available.",
    store_url: "https://apps.apple.com/app/id1234567890",
  },
};

const AppVersionForm = () => {
  const [platform, setPlatform] = useState("android");
  const [formData, setFormData] = useState(initialData);
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      status: true,
      [platform]: formData[platform],
    };

    setSaving(true);
    try {
      await axiosConfig({
        method: "post",
        url: "/appdashboard/update-config/save/",
        data: payload,
      });
      toast.success("Config saved successfully!");
    } catch (error) {
      const errMsg =
        error?.response?.data?.response ||
        error?.response?.data?.message ||
        error.message ||
        "Failed to save config.";
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const current = formData[platform];

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          App Version Control
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Platform Dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Platform</InputLabel>
          <Select
            value={platform}
            label="Platform"
            onChange={(e) => setPlatform(e.target.value)}
          >
            <MenuItem value="android">Android</MenuItem>
            <MenuItem value="ios">iOS</MenuItem>
          </Select>
        </FormControl>

        {/* Latest Version */}
        <TextField
          label="Latest Version"
          fullWidth
          sx={{ mb: 2 }}
          value={current.latest_version}
          onChange={(e) => handleChange("latest_version", e.target.value)}
        />

        {/* Minimum Supported Version */}
        <TextField
          label="Minimum Supported Version"
          fullWidth
          sx={{ mb: 2 }}
          value={current.minimum_supported_version}
          onChange={(e) =>
            handleChange("minimum_supported_version", e.target.value)
          }
        />

        {/* Force Update */}
        <FormControl sx={{ mb: 2 }}>
          <Typography fontSize={14} fontWeight={500} mb={1}>
            Force Update
          </Typography>
          <RadioGroup
            row
            value={current.force_update ? "true" : "false"}
            onChange={(e) =>
              handleChange("force_update", e.target.value === "true")
            }
          >
            <FormControlLabel value="true" control={<Radio />} label="True" />
            <FormControlLabel value="false" control={<Radio />} label="False" />
          </RadioGroup>
        </FormControl>

        {/* Update Title */}
        <TextField
          label="Update Title"
          fullWidth
          sx={{ mb: 2 }}
          value={current.update_title}
          onChange={(e) => handleChange("update_title", e.target.value)}
        />

        {/* Update Message */}
        <TextField
          label="Update Message"
          fullWidth
          multiline
          rows={3}
          sx={{ mb: 2 }}
          value={current.update_message}
          onChange={(e) => handleChange("update_message", e.target.value)}
        />

        {/* Store URL */}
        <TextField
          label="Store URL"
          fullWidth
          sx={{ mb: 3 }}
          value={current.store_url}
          onChange={(e) => handleChange("store_url", e.target.value)}
        />

        <Button
          variant="contained"
          fullWidth
          color="success"
          size="large"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Save Changes"
          )}
        </Button>
      </CardContent>
    </Box>
  );
};

export default AppVersionForm;
